// Chuỗi migration (TECH_BASELINE §7.3). Khóa = phiên bản NGUỒN. Hàm thuần, không đọc state
// toàn cục, không phụ thuộc giờ. Không bao giờ di trú bằng cách tịch thu thứ người chơi đã có.
import { SAVE_VERSION, isPlainObject, type RawDoc, type SaveDoc } from './types';
import { validateDoc } from './validate';

export class FutureVersionError extends Error {
  constructor(readonly version: number) {
    super(`save version ${version} is newer than supported ${SAVE_VERSION}`);
    this.name = 'FutureVersionError';
  }
}

export class MigrationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MigrationError';
  }
}

export const MIGRATIONS: Readonly<Record<number, (d: RawDoc) => RawDoc>> = {
  /**
   * v1 → v2: gom cờ trạng thái vào sổ `core.ledger` và thêm sổ `claimed` (chống trao thưởng
   * hai lần). v1 để cờ rời ở `core.flags` (nếu có); giữ nguyên mọi cờ, không lọc bỏ.
   */
  1: (d) => {
    const core = isPlainObject(d.core) ? { ...d.core } : {};
    const flags = isPlainObject(core.flags) ? { ...core.flags } : {};
    delete core.flags;
    return { ...d, v: 2, core: { ...core, ledger: { flags, claimed: [] } } };
  },
  /**
   * v2 → v3 (M1): thêm slice tu luyện và cốt truyện. Người chơi M0 chưa có tiến trình tu luyện:
   * tu luyện bắt đầu từ LK1, mốc quyết toán = lúc lưu cuối (không tính bù thời gian trước M1).
   * Tiến độ mở đầu suy từ cờ đã có: đã thành Đăng Căn → tiếp ở A8 (bước trao thưởng tân thủ và mục
   * Điển Tịch, để không mất thưởng); đã thắp đèn → tiếp từ câu hỏi đầu.
   * Giá trị viết thẳng (đông cứng theo v3), không import mặc định của sim.
   */
  2: (d) => {
    const core = isPlainObject(d.core) ? { ...d.core } : {};
    const at = typeof d.savedAt === 'number' && Number.isFinite(d.savedAt) ? d.savedAt : 0;
    const ledger = isPlainObject(core.ledger) ? core.ledger : {};
    const flags = isPlainObject(ledger.flags) ? ledger.flags : {};
    if (!isPlainObject(core.cultivation))
      core.cultivation = {
        step: 0,
        cultivationXp: 0,
        overflowQi: 0,
        idle: {
          mode: 'seclusion',
          locationId: 'site.lk.servantQuarters',
          seclusion: { kind: 'light', startedAtMs: at, endsAtMs: null, lifespanPrepaid: 0 },
          lastSettledMs: at,
        },
        lifespan: { age: 16, lifespanBonus: 0, pillsTakenByRealm: [0, 0, 0, 0, 0, 0, 0, 0, 0], history: [] },
        technique: {
          mainId: 'tech.qiGuiding',
          layer: 1,
          mastery: 0,
          known: ['tech.qiGuiding'],
          triOrigin: { body: 0, spirit: 0, mind: 0 },
        },
        gatherArrayLv: 0,
        buffs: [],
      };
    if (!isPlainObject(core.story)) {
      const beat = flags.t1_dang_can_formed === true ? 'a8' : flags.t1_lamp_lit === true ? 'a6' : 'a1';
      core.story = { opening: { beat, done: false }, codex: [] };
    }
    return { ...d, v: 3, core };
  },
  /**
   * v3 → v4 (M2): thêm Hành Trang (`core.inventory`), Lịch Luyện (`core.trial`), Động Phủ
   * (`core.cave`); tu luyện thêm ô Lịch Luyện, đan độc, phần trang bị. Người chơi đã xong mở đầu ở M1
   * nhận bộ khởi đầu của Đăng Linh (Tụ Khí Đan, Hồi Xuân Đan, hạt giống) ngay trong migration và
   * đánh dấu đã trao; ai còn ở giữa mở đầu nhận ở cảnh A8 như người mới. Giá trị viết thẳng (đông cứng v4).
   */
  3: (d) => {
    const core = isPlainObject(d.core) ? { ...d.core } : {};
    const at = typeof d.savedAt === 'number' && Number.isFinite(d.savedAt) ? d.savedAt : 0;
    const ledger = isPlainObject(core.ledger) ? { ...core.ledger } : { flags: {}, claimed: [] };
    const claimed = Array.isArray(ledger.claimed) ? [...(ledger.claimed as unknown[])] : [];
    const story = isPlainObject(core.story) ? core.story : {};
    const opening = isPlainObject(story.opening) ? story.opening : {};
    const kitKey = 'gift.lampSpirit.startKit';
    const goods: Record<string, number> = {};
    if (opening.done === true && !claimed.includes(kitKey)) {
      Object.assign(goods, {
        'pill.qiGathering#mid': 3,
        'pill.springRevival#mid': 5,
        'seed.dewGrass': 2,
        'seed.yellowEssence': 2,
      });
      claimed.push(kitKey);
    }
    core.ledger = { ...ledger, claimed };
    if (isPlainObject(core.cultivation)) {
      const c = { ...core.cultivation };
      const idle = isPlainObject(c.idle) ? { ...c.idle } : {};
      if (!('trialZoneId' in idle)) idle.trialZoneId = null;
      c.idle = idle;
      if (typeof c.pillToxicity !== 'number') c.pillToxicity = 0;
      if (!isPlainObject(c.gear)) c.gear = { qiRatePct: 0, offlineEffPct: 0 };
      core.cultivation = c;
    }
    const slots = [
      'weapon',
      'helm',
      'robe',
      'pants',
      'gloves',
      'boots',
      'necklace',
      'ring1',
      'ring2',
      'treasure',
    ];
    if (!isPlainObject(core.inventory))
      core.inventory = {
        equipped: Object.fromEntries(slots.map((s) => [s, null])),
        bag: [],
        goods,
        filter: { enabled: false, maxRarity: 'mortal', requireNoHighTier: false, giaiGap: null },
        unopened: [],
        fresh: [],
      };
    else if (Object.keys(goods).length > 0) {
      // Doc đã có Hành Trang (bản thử / nhập tay): bộ khởi đầu cộng vào kho sẵn có, không mất quà dù
      // đã đánh dấu `claimed`.
      const inv = { ...core.inventory };
      const g: Record<string, unknown> = isPlainObject(inv.goods) ? { ...inv.goods } : {};
      for (const [k, n] of Object.entries(goods)) {
        const cur = g[k];
        g[k] = (typeof cur === 'number' && Number.isFinite(cur) && cur > 0 ? Math.floor(cur) : 0) + n;
      }
      inv.goods = g;
      core.inventory = inv;
    }
    if (!isPlainObject(core.trial))
      core.trial = {
        zones: {
          'res.thanhHuVein': { reserve: 100, atMs: at, gatherFrac: {} },
          'res.langCoc': { reserve: 100, atMs: at, gatherFrac: {} },
        },
        kills: {},
        killFrac: 0,
      };
    if (!isPlainObject(core.cave))
      core.cave = {
        buildings: { herbGarden: { lv: 1, upgrade: null }, pillRoom: { lv: 1, upgrade: null } },
        plots: [0, 1, 2, 3].map(() => ({ herbId: null, plantedAtMs: 0, bonusYears: 0 })),
        alchemy: { lv: 1, xp: 0, knownRecipes: [] },
        jobs: [],
        jobSeq: 0,
      };
    return { ...d, v: 4, core };
  },
};

/** Đọc số phiên bản; ném MigrationError nếu doc không có dạng save. */
export function docVersion(raw: unknown): number {
  if (!isPlainObject(raw)) throw new MigrationError('save is not an object');
  const v = raw.v;
  if (typeof v !== 'number' || !Number.isInteger(v) || v < 1)
    throw new MigrationError(`bad version: ${String(v)}`);
  return v;
}

/** Di trú một doc thô lên SAVE_VERSION rồi kiểm hình. */
export function migrate(raw: unknown): { doc: SaveDoc; from: number } {
  const from = docVersion(raw);
  if (from > SAVE_VERSION) throw new FutureVersionError(from);
  let d = raw as RawDoc;
  for (let v = from; v < SAVE_VERSION; v++) {
    const step = MIGRATIONS[v];
    if (!step) throw new MigrationError(`no migration path from v${v}`);
    d = step(d);
    if (d.v !== v + 1) throw new MigrationError(`migration ${v} produced v${String(d.v)}`);
  }
  return { doc: validateDoc(d), from };
}
